import { useEffect, useState } from "react";

import { Modal } from "@covertree/ui";
import { Form } from "@covertree/ui";

import {
  CoverageFilesFormData,
  createValidationSchemaForUploadProofOfCoverageDataForm,
} from "@forms";
import { isCoverageDocument } from "@utils";

import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useAppSelector from "@redux/hooks/useAppSelector";
import useAppDispatch from "@redux/hooks/useAppDispatch";
import { useUploadResidentDocumentsMutation } from "@redux/documents/documentsRestApi";
import {
  resetExternalCoverageData,
  saveExternalPolicyDocuments,
  selectDocumentsUploadStatus,
  selectExternalPolicyDocuments,
} from "@redux/coverages/coveragesSlice";

import UploadProofOfCoverageModalContent from "./UploadProofOfCoverageModalContent";

interface UploadProofOfCoverageModalProps {
  open: boolean;
  onClose: () => void;
  residentId: string;
}

function UploadProofOfCoverageModal({
  open,
  onClose,
  residentId,
}: UploadProofOfCoverageModalProps) {
  const dispatch = useAppDispatch();

  const savedFiles = useAppSelector(selectExternalPolicyDocuments);
  const { hasDocumentsUploadFailed: isFailed, description } = useAppSelector(
    selectDocumentsUploadStatus
  );

  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [isUploadSuccess, setIsUploadSuccess] = useState(false);

  const [uploadResidentDocuments, { reset }] =
    useUploadResidentDocumentsMutation();

  const form = useForm({
    defaultValues: { description, files: savedFiles },
    mode: "all",
    resolver: yupResolver(
      createValidationSchemaForUploadProofOfCoverageDataForm()
    ),
  });

  const { handleSubmit, reset: resetForm } = form;

  useEffect(() => {
    resetForm({ files: savedFiles, description });
  }, [savedFiles]);

  const submit = async ({ files, description }: CoverageFilesFormData) => {
    setIsUploadLoading(true);

    const results = await Promise.all(
      files.map((file) =>
        uploadResidentDocuments({
          file: file,
          residentId: residentId,
          description: description,
        })
      )
    );

    const failedFiles = files?.filter((file) => {
      const result = results.find((res) => res?.["data"]?.name === file.name);
      return !isCoverageDocument(result?.["data"]);
    });

    if (!!failedFiles?.length) {
      dispatch(
        saveExternalPolicyDocuments({
          files: failedFiles,
          isFailed: true,
          description: description,
        })
      );
    } else {
      setIsUploadSuccess(true);
    }
    setIsUploadLoading(false);
  };

  const handleClose = () => {
    onClose();
    reset();
    setIsUploadSuccess(false);
    dispatch(resetExternalCoverageData());
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      maxWidth="md"
      hideCloseButtonAndTitle
      sx={{
        paddingTop: {
          xs: 12,
          sm: 3,
        },
      }}>
      <Form onSubmit={handleSubmit(submit)}>
        <FormProvider {...form}>
          <UploadProofOfCoverageModalContent
            onClose={handleClose}
            isLoading={isUploadLoading}
            isError={isFailed}
            isSuccess={isUploadSuccess}
          />
        </FormProvider>
      </Form>
    </Modal>
  );
}

export default UploadProofOfCoverageModal;
